import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../hooks';
import { useAuthStore } from '../stores';
import { getRatingTier, getInitials } from '@callbridge/ui';
import { endpoints } from '@callbridge/ui';
import {
  SpadeSvg,
  HeartSvg,
  DiamondSvg,
  ClubSvg,
  BackArrowSvg,
  LogoutSvg,
} from '../components/SuitIcons';

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  displayName: string;
  rating: number;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
}

interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UserRankData {
  rank: { rank: number; total: number; percentile: number } | null;
}

export function LeaderboardPage() {
  const { request } = useApiClient();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [myRank, setMyRank] = useState<{ rank: number; total: number; percentile: number } | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const limit = 20;

  const fetchLeaderboard = useCallback(
    async (p: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await request<{ data: LeaderboardData }>(endpoints.getLeaderboard(p, limit));
        setEntries(res.data.leaderboard);
        setTotalPages(res.data.pagination.totalPages);
        setTotal(res.data.pagination.total);
        setPage(p);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    },
    [request],
  );

  const fetchMyRank = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await request<{ data: UserRankData }>(endpoints.getMyRank());
      setMyRank(res.data.rank);
    } catch {
      // Non-critical — user might not have played yet
    }
  }, [request, isAuthenticated]);

  useEffect(() => {
    fetchLeaderboard(1);
    fetchMyRank();
  }, [fetchLeaderboard, fetchMyRank]);

  return (
    <div className="page leaderboard-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      <div className="page-topbar">
        <button className="page-back" onClick={() => navigate('/')}>
          <BackArrowSvg />
          Back
        </button>
        <div className="page-topbar-right">
          {user && <span className="page-username">♠ {user.username}</span>}
          <button className="page-logout" onClick={logout}>
            <LogoutSvg />
            Logout
          </button>
        </div>
      </div>

      <div className="leaderboard-header">
        <h1>Leaderboard</h1>
        <span className="leaderboard-total">{total} players ranked</span>
      </div>

      {/* User's own rank card */}
      {isAuthenticated && myRank && (
        <div className="my-rank-card">
          <div className="my-rank-position">
            <span className="rank-number">#{myRank.rank}</span>
            <span className="rank-total">of {myRank.total}</span>
          </div>
          <div className="my-rank-info">
            <span className="my-rank-name">{user?.username}</span>
            <span className="my-rank-percentile">Top {100 - myRank.percentile}%</span>
          </div>
          {user && (
            <div className="my-rank-rating">
              <span className={`tier-badge tier-${getRatingTier(user.rating).color}`}>
                {getRatingTier(user.rating).label}
              </span>
              <span className="rating-value">{user.rating}</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="auth-error" role="alert">
          {error}
          <div className="error-retry-row">
            <button className="btn btn-sm btn-outline" onClick={() => fetchLeaderboard(page)}>
              Retry
            </button>
            <button className="error-dismiss" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-indicator" role="status" aria-live="polite">
          Loading leaderboard...
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <p>No ranked players yet. Play some games to get on the board!</p>
        </div>
      ) : (
        <>
          <div className="leaderboard-table">
            <div className="leaderboard-table-header">
              <span className="lb-col-rank">#</span>
              <span className="lb-col-player">Player</span>
              <span className="lb-col-rating">Rating</span>
              <span className="lb-col-games">Games</span>
              <span className="lb-col-winrate">Win %</span>
            </div>
            {entries.map((entry) => {
              const tier = getRatingTier(entry.rating);
              const isMe = user?.id === entry.userId;
              return (
                <div
                  key={entry.userId}
                  className={`leaderboard-row ${isMe ? 'leaderboard-row-me' : ''} ${entry.rank <= 3 ? `leaderboard-top-${entry.rank}` : ''}`}
                >
                  <span className="lb-col-rank">
                    {entry.rank <= 3 ? (
                      <span className={`rank-medal rank-${entry.rank}`}>
                        {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                      </span>
                    ) : (
                      entry.rank
                    )}
                  </span>
                  <span className="lb-col-player">
                    <span className="lb-avatar">
                      {getInitials(entry.displayName || entry.username)}
                    </span>
                    <span className="lb-name">
                      {entry.displayName || entry.username}
                      {isMe && <span className="lb-you-badge">You</span>}
                    </span>
                  </span>
                  <span className="lb-col-rating">
                    <span className={`tier-dot tier-${tier.color}`} />
                    {entry.rating}
                  </span>
                  <span className="lb-col-games">{entry.gamesPlayed}</span>
                  <span className="lb-col-winrate">{entry.winRate}%</span>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="leaderboard-pagination">
              <button
                className="btn btn-outline btn-sm"
                disabled={page <= 1}
                onClick={() => fetchLeaderboard(page - 1)}
              >
                ← Prev
              </button>
              <span className="pagination-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-outline btn-sm"
                disabled={page >= totalPages}
                onClick={() => fetchLeaderboard(page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
